import { useRef, useState } from "react";
import ToolTipUsingPortal from "../../components/toolTipUsingPortal/toolTipUsingPortal";
import FileIcon from "../../svg/fileIcon";
import { InfoIcon } from "../../svg/info-icon";
import RedWarningIcon from "../../svg/redWarningIcon";
import NextStepAccordion, { NextStep } from "./nextStepAccordion";
import { getBucketColorClasses } from "./outcomeSummaryUtils";

interface FirstStpOutcomeData {
  bucket?: string;
  sub_bucket?: string;
  keywords_matched?: string[];
  manual_review_needed?: boolean;
  phone_numbers?: string[];
  fax_numbers?: string[];
  urls?: string[];
  platforms?: string[];
}

interface OutcomeSummaryPanelProps {
  outcomeData: FirstStpOutcomeData | string | null | undefined;
  comment: string | null | undefined;
}

const getNextSteps = (
  outcomeData: FirstStpOutcomeData | null | undefined,
): NextStep[] => {
  if (!outcomeData) return [];

  const steps: NextStep[] = [];

  if (outcomeData.phone_numbers && outcomeData.phone_numbers.length > 0) {
    steps.push({ title: "Call Payer", values: outcomeData.phone_numbers });
  }

  if (outcomeData.fax_numbers && outcomeData.fax_numbers.length > 0) {
    steps.push({ title: "Fax Documents", values: outcomeData.fax_numbers });
  }

  if (outcomeData.urls && outcomeData.urls.length > 0) {
    steps.push({ title: "Visit Portal", values: outcomeData.urls });
  }

  if (outcomeData.platforms && outcomeData.platforms.length > 0) {
    steps.push({ title: "Use Platform", values: outcomeData.platforms });
  }

  return steps;
};

const parseOutcomeData = (
  data: FirstStpOutcomeData | string | null | undefined,
): FirstStpOutcomeData | null => {
  if (!data || typeof data === "string") return null;
  return data as FirstStpOutcomeData;
};

const OutcomeSummaryPanel = ({
  outcomeData,
  comment,
}: OutcomeSummaryPanelProps) => {
  const parsed = parseOutcomeData(outcomeData);
  const bucket = parsed?.bucket || "--";
  const subBucket = parsed?.sub_bucket;
  const manualReviewNeeded = parsed?.manual_review_needed;
  const keywordsMatched = parsed?.keywords_matched;
  const nextSteps = getNextSteps(parsed);

  const [showKeywordsTooltip, setShowKeywordsTooltip] = useState(false);
  const keywordsIconRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="rounded-lg border border-primaryGray-14">
        <div className="text-y flex items-center justify-between border-b border-primaryGray-14 px-4 py-3">
          <div className="flex items-center gap-2">
            <FileIcon strokeColor="#2E2E2E" />
            <span className="text-h12 font-bold text-primaryGray-2">
              Summary
            </span>
          </div>
          {manualReviewNeeded && (
            <div className="inline-flex items-center gap-1 rounded-3xl border border-tertiaryRed-3 bg-red-50 px-2 py-1 text-small font-semibold text-tertiaryRed-3">
              <RedWarningIcon width={12} height={12} />
              Manual Review Needed
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 p-4">
          <div>
            <div className="mb-1.5 flex items-center gap-1.5 text-overline font-bold uppercase tracking-wider text-primaryGray-8">
              Outcome
              {keywordsMatched && keywordsMatched.length > 0 && (
                <div
                  ref={keywordsIconRef}
                  className="inline-flex cursor-pointer"
                  onMouseEnter={() => setShowKeywordsTooltip(true)}
                  onMouseLeave={() => setShowKeywordsTooltip(false)}
                >
                  <InfoIcon
                    width="14"
                    height="14"
                    stroke="#6B7280"
                    className="hover:opacity-70"
                  />
                  <ToolTipUsingPortal
                    showTooltip={showKeywordsTooltip}
                    parentRef={keywordsIconRef}
                    placement="right"
                    wrapText={true}
                    maxWidth="300px"
                    offsetPx={8}
                  >
                    <span>{keywordsMatched.join(", ")}</span>
                  </ToolTipUsingPortal>
                </div>
              )}
            </div>
            <span
              className={`inline-block rounded-md px-2.5 py-0.5 text-small font-bold ${getBucketColorClasses(bucket)}`}
            >
              {bucket}
            </span>
            {subBucket && (
              <div className="ml-2 mt-1.5 inline-block rounded-md bg-primaryGray-16 px-2.5 py-0.5 text-small font-semibold text-primaryGray-2">
                {subBucket}
              </div>
            )}
          </div>

          <div>
            <div className="mb-1.5 text-overline font-bold uppercase tracking-wider text-primaryGray-8">
              Comment
            </div>
            <div className="rounded-md border border-primaryGray-14 p-3 text-small leading-relaxed text-primaryGray-3">
              {comment || "No comment available."}
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="mb-2 text-overline font-bold uppercase tracking-wider text-primaryGray-8">
          Suggested Next Steps
        </div>
        {nextSteps.length > 0 ? (
          <div className="flex flex-col gap-2">
            {nextSteps.map((step, index) => (
              <NextStepAccordion key={index} step={step} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-primaryGray-14 px-4 py-3 text-small text-primaryGray-8">
            No suggested next steps available for this outcome.
          </div>
        )}
      </div>
    </div>
  );
};

export { OutcomeSummaryPanel };
export type { FirstStpOutcomeData };
